import React, { DetailedHTMLProps, HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { fromEvent, Observable, Observer } from 'rxjs';
import { switchMap, takeUntil, pairwise, tap, throwIfEmpty } from 'rxjs/operators';
import { number } from 'yup';

type DrawingCanvasProps = {
    onStartDrawing: () => void,
    onClearDrawing: () => void,
    placeholder: string,
    height?: number,
    width?: number,
    minLineRequired?: number,
    onGetImageCallback: ({ getDataURL, clearDrawing }: {
        getDataURL: () => string | undefined,
        clearDrawing: () => void
    }) => void,
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onStartDrawing, onClearDrawing, placeholder, height = 300, width = 500, minLineRequired = 150, onGetImageCallback }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [hasStartedDrawing, setHasStartedDrawing] = useState(false)

    const totalLine = useRef(0);
    const contextRef = useRef<CanvasRenderingContext2D | null>();

    const updateTotalPath = useCallback((prevPosx: number, currentPosx: number, prevPosy: number, currentPosy: number): void => {
        totalLine.current = totalLine.current + Math.abs(currentPosx - prevPosx) + Math.abs(currentPosy - prevPosy)
    }, [])


    const drawOnCanvas = useCallback((prevPos: { x: number, y: number }, currentPos: { x: number, y: number }): void => {
        if (!contextRef.current) { return; }

        contextRef.current.beginPath();

        if (prevPos) {
            contextRef.current.moveTo(prevPos.x, prevPos.y); // from
            contextRef.current.lineTo(currentPos.x, currentPos.y);
            contextRef.current.stroke();
        }
    }, [])

    const clearDrawing = useCallback((): void => {
        onClearDrawing();
        if (!canvasRef.current) {
            return
        }

        const context = canvasRef.current.getContext('2d');
        context?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setHasStartedDrawing(false)
        totalLine.current = 0
    }, [])

    const getDataURL = useCallback((): string | undefined => {
        return canvasRef.current?.toDataURL('image/png')
    }, [])

    const captureEvents = useCallback((canvasEl: HTMLCanvasElement) => {
        // this will capture all mousedown events from the canvas element
        fromEvent(canvasEl, 'mousedown')
            .pipe(
                tap(() => {
                    setHasStartedDrawing(true)
                    // setIsSkipped(false)
                }),
                switchMap((e) => {
                    // after a mouse down, we'll record all mouse moves
                    return fromEvent(canvasEl, 'mousemove')
                        .pipe(
                            // we'll stop (and unsubscribe) once the user releases the mouse
                            // this will trigger a 'mouseup' event
                            takeUntil(fromEvent(canvasEl, 'mouseup')),
                            // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
                            takeUntil(fromEvent(canvasEl, 'mouseleave')),
                            // pairwise lets us get the previous value to draw a line from
                            // the previous point to the current point
                            pairwise()
                        )
                })
            )
            .subscribe((res: [MouseEvent, MouseEvent]) => {
                const rect = canvasEl.getBoundingClientRect();

                // previous and current position with the offset
                const prevPos = {
                    x: res[0].clientX - rect.left,
                    y: res[0].clientY - rect.top
                };

                const currentPos = {
                    x: res[1].clientX - rect.left,
                    y: res[1].clientY - rect.top
                };

                // this method we'll implement soon to do the actual drawing
                drawOnCanvas(prevPos, currentPos);
                updateTotalPath(prevPos.x, currentPos.x, prevPos.y, currentPos.y);
                if (totalLine.current > minLineRequired) {
                    onStartDrawing();
                }
            });
    }, [])

    const captureGestureEvents = useCallback((canvasEl: HTMLCanvasElement): void => {
        // this will capture all mousedown events from the canvas element
        fromEvent(canvasEl, 'touchstart')
            .pipe(
                tap(() => {
                    setHasStartedDrawing(true)
                    // setIsSkipped(false)
                }),
                switchMap((e) => {
                    // after a mouse down, we'll record all mouse moves
                    return fromEvent(canvasEl, 'touchmove')
                        .pipe(
                            // we'll stop (and unsubscribe) once the user releases the mouse
                            // this will trigger a 'mouseup' event
                            takeUntil(fromEvent(canvasEl, 'touchend')),
                            // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
                            takeUntil(fromEvent(canvasEl, 'touchcancel')),
                            // pairwise lets us get the previous value to draw a line from
                            // the previous point to the current point
                            pairwise()
                        )
                })
            )
            .subscribe((res: [TouchEvent, TouchEvent]) => {
                const rect = canvasEl.getBoundingClientRect();

                // previous and current position with the offset
                const prevPos = {
                    x: res[0].changedTouches[0].clientX - rect.left,
                    y: res[0].changedTouches[0].clientY - rect.top
                };

                const currentPos = {
                    x: res[1].changedTouches[0].clientX - rect.left,
                    y: res[1].changedTouches[0].clientY - rect.top
                };

                // this method we'll implement soon to do the actual drawing
                drawOnCanvas(prevPos, currentPos);
                updateTotalPath(prevPos.x, currentPos.x, prevPos.y, currentPos.y);
                if (totalLine.current > minLineRequired) {
                    onStartDrawing()
                }
            });
    }, [])


    useEffect(() => {
        setTimeout(() => {
            if (!canvasRef.current || !wrapperRef.current) {
                return
            }

            const canvasEl: HTMLCanvasElement = canvasRef.current;
            const cx = canvasEl.getContext('2d');

            contextRef.current = cx;

            const widthAvailable = window.outerWidth;

            if (widthAvailable < 768) {
                canvasEl.width = widthAvailable - 40;
                const wrapperEl: HTMLDivElement = wrapperRef.current;
                canvasEl.height = wrapperEl.clientHeight - 20;
            } else {
                const wrapperEl: HTMLDivElement = wrapperRef.current;
                canvasEl.width = wrapperEl.clientWidth;
                canvasEl.height = height;
            }

            if (cx) {
                cx.lineWidth = 4;
                cx.lineCap = 'round';
                cx.strokeStyle = '#3DA2BE';
            }

            captureEvents(canvasEl);
            captureGestureEvents(canvasEl);

            onGetImageCallback({ getDataURL, clearDrawing })
        });
    }, [canvasRef.current, wrapperRef.current])

    return (
        <div id="wrapper" className="position-relative d-flex h-100" ref={wrapperRef}>
            {hasStartedDrawing && (
                <button type="button" aria-label="clear signature and retry" className="position-absolute btn mr-2 mb-2" onClick={clearDrawing} style={{ bottom: 0, right: 0 }}>
                    <img src="/img/svg/retry.svg" alt=""/>
                </button>)
            }

            {!hasStartedDrawing && (<span className="avoid-clicks position-absolute d-block text-center w-100 text-muted small p-4" style={{ top: '35%', pointerEvents: 'none' }}>
                { placeholder}
            </span>)}

            <div className="border-dashed border-gray border-3 rounded">
                <canvas className="d-block w-100 h-100" id="canvas" ref={canvasRef} />
            </div>
        </div>

    )
}

export default DrawingCanvas;
